import { Link, useNavigate, useParams } from "react-router-dom";
import {
  eachDayOfInterval,
  format,
  isSameDay,
  parseISO,
  subDays,
} from "date-fns";
import { useAuth } from "../auth/AuthProvider";
import { useGoal, useProfiles, useWeekPeriods, weekProgress } from "./api";
import { useGoalCheckIns, usePhotoUrl, useUndoCheckIn } from "../checkins/api";
import { CheckInButton } from "../checkins/CheckInButton";
import { PokeButton } from "../nudges/PokeButton";
import { BackButton } from "../../components/AppShell";
import {
  EmptyState,
  Eyebrow,
  HistoryGrid,
  ProgressPips,
  Skeleton,
  Spinner,
  type HistoryDay,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import {
  cadenceLabel,
  daysSince,
  dayLabel,
  relativeTime,
} from "../../lib/time";
import type { CheckIn } from "../../lib/database.types";
import { errorMessage } from "../../lib/errors";

/** Two rows of thirteen, as the design draws it. */
const HISTORY_DAYS = 26;

export function GoalDetailPage() {
  const { goalId } = useParams<{ goalId: string }>();
  const { userId } = useAuth();
  const navigate = useNavigate();

  const goal = useGoal(goalId);
  const profiles = useProfiles();
  const checkIns = useGoalCheckIns(goalId);
  const periods = useWeekPeriods();

  if (goal.isLoading) {
    return (
      <div className="flex flex-col gap-3 pt-safe">
        <Skeleton className="mt-4 h-24" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (!goal.data) {
    return (
      <div className="pt-safe">
        <EmptyState
          title="That goal isn't here."
          action={
            <Link to="/" className="btn btn-secondary no-underline">
              back to the feed
            </Link>
          }
        />
      </div>
    );
  }

  const {
    title,
    description,
    cadence,
    target_per_period,
    owner_id,
    is_archived,
    stats,
    starts_on,
  } = goal.data;
  const owner = profiles.data?.find((profile) => profile.id === owner_id);
  const isMine = owner_id === userId;

  const entries = checkIns.data ?? [];
  const week = weekProgress(goal.data, periods.data);
  const history = buildHistory(entries, starts_on);
  const quietDays = stats?.last_check_in_at
    ? daysSince(stats.last_check_in_at)
    : null;

  return (
    <div className="pt-safe">
      <header className="flex items-center justify-between gap-2 pb-1">
        <BackButton onClick={() => navigate(-1)} />
        <h1 className="min-w-0 flex-1 truncate text-center text-lg">
          {isMine ? title : `${owner?.display_name ?? "…"} — ${title}`}
        </h1>
        {isMine ? (
          is_archived ? (
            <span className="w-[18px]" />
          ) : (
            <Link
              to={`/goals/${goalId}/edit`}
              className="text-[13px] text-accent-700 no-underline"
            >
              edit
            </Link>
          )
        ) : owner && !is_archived ? (
          <PokeButton
            targetId={owner.id}
            targetName={owner.display_name}
            goalId={goalId}
          />
        ) : (
          <span className="w-[18px]" />
        )}
      </header>

      <p className="text-center text-xs opacity-50">
        {cadenceLabel(cadence, target_per_period)}
        {is_archived && " · archived"}
      </p>
      {stats?.period_end && !is_archived && (
        <p className="pb-3 text-center text-xs opacity-65">
          resets {resetLabel(stats.period_end)}
        </p>
      )}
      {description && (
        <p className="pb-3 pt-2 text-center text-[13px] opacity-70">
          {description}
        </p>
      )}

      <section
        aria-labelledby="this-week"
        className="flex flex-col gap-1.5 py-3"
      >
        <Eyebrow>
          <span id="this-week">this week</span>
        </Eyebrow>
        <div className="big-num text-3xl">
          {week.done}/{week.target}
        </div>
        <ProgressPips value={week.done} target={week.target} className="mt-1" />
      </section>

      <section aria-labelledby="history" className="flex flex-col gap-2 py-3">
        <Eyebrow>
          <span id="history">history</span>
        </Eyebrow>
        <HistoryGrid days={history} />
        {quietDays !== null && quietDays >= 1 && (
          <p className="text-xs opacity-50">
            {quietDays} {quietDays === 1 ? "day" : "days"} quiet
          </p>
        )}
      </section>

      {isMine && !is_archived && (
        <div className="py-3">
          <CheckInButton goal={goal.data} ownerId={userId!} block />
        </div>
      )}

      <section aria-labelledby="past" className="flex flex-col gap-2 py-3">
        <Eyebrow>
          <span id="past">past check-ins</span>
        </Eyebrow>

        {checkIns.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : entries.length ? (
          <ul>
            {entries.map((entry, index) => (
              <li key={entry.id}>
                <CheckInDetail
                  entry={entry}
                  canUndo={isMine}
                  last={index === entries.length - 1}
                />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="No check-ins yet." />
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The last 26 days, oldest first. Days outside the goal's life and the
 * trailing run still waiting on a check-in are left open rather than marked
 * as misses — neither is a failure yet.
 */
function buildHistory(entries: CheckIn[], startsOn: string): HistoryDay[] {
  const today = new Date();
  const start = parseISO(startsOn);
  const days = eachDayOfInterval({
    start: subDays(today, HISTORY_DAYS - 1),
    end: today,
  });

  const result: HistoryDay[] = days.map((day) => {
    if (day < start && !isSameDay(day, start)) return "open";
    return entries.some((entry) =>
      isSameDay(parseISO(entry.checked_in_at), day),
    )
      ? "done"
      : "missed";
  });

  // Walk back from today over the current quiet run and soften it.
  for (let i = result.length - 1; i >= 0 && result[i] === "missed"; i -= 1) {
    result[i] = "open";
  }

  return result;
}

function resetLabel(periodEnd: string): string {
  const end = parseISO(periodEnd);
  const inDays = Math.round((end.getTime() - Date.now()) / 86_400_000);
  if (inDays <= 0) return "today";
  if (inDays === 1) return "tomorrow";
  return format(end, "EEEE");
}

function CheckInDetail({
  entry,
  canUndo,
  last,
}: {
  entry: CheckIn;
  canUndo: boolean;
  last: boolean;
}) {
  const photo = usePhotoUrl(entry.photo_path);
  const undo = useUndoCheckIn();
  const toast = useToast();

  return (
    <div className={last ? "py-2" : "row-rule py-2"}>
      <div className="flex items-baseline gap-3">
        <span className="min-w-0 flex-1 text-[13px] opacity-75">
          {entry.note || "checked in"}
        </span>
        <span className="shrink-0 text-xs opacity-50">
          {dayOrAge(entry.checked_in_at)}
        </span>
        {canUndo && (
          <button
            type="button"
            aria-label="Remove this check-in"
            disabled={undo.isPending}
            onClick={() =>
              undo.mutate(entry, {
                onSuccess: () => toast("Check-in removed"),
                onError: (error) =>
                  toast(errorMessage(error, "Could not remove"), "error"),
              })
            }
            className="shrink-0 text-[11px] opacity-50 transition hover:text-accent-800 hover:opacity-100"
          >
            {undo.isPending ? <Spinner className="size-3" /> : "undo"}
          </button>
        )}
      </div>

      {entry.photo_path && photo.data && (
        <img
          src={photo.data}
          alt=""
          loading="lazy"
          className="washed mt-2 max-h-56 w-full rounded-lg object-cover"
        />
      )}
    </div>
  );
}

/** "today" and "yesterday" read better than "0d ago" at the end of a row. */
function dayOrAge(iso: string): string {
  const label = dayLabel(iso);
  if (label === "Today") return "today";
  if (label === "Yesterday") return "yesterday";
  return relativeTime(iso);
}
